import { normalizeStorageError, startUpload, UploadFileInput, UploadTask } from '@Utils/S3Storage'
import { v4 as uuidv4 } from 'uuid'
import toastr from 'toastr'

import FileNameSanitizer from '@Utils/FileNameSanitizer'

import 'toastr/build/toastr.css'

export interface IAvatarUploaderOptions {
  readonly bucketName: string
  uploadAvatarCallback: (params: IUploadedAvatar) => void
  uploadingAvatarCallback?: () => void
}

export interface IUploadedAvatar {
  readonly url: string
}

class AvatarUploader {
  bucketName: string
  filename: string
  sanitizedFileName: string
  uploadAvatarCallback: (params: IUploadedAvatar) => void
  uploadingAvatarCallback?: () => void

  constructor(
    private readonly file: File,
    options: IAvatarUploaderOptions,
  ) {
    this.file = file
    this.filename = decodeURI(encodeURI(this.file.name))
    // we have to take only the first 50 chars to suppress the error for tempfiles:
    // Filename too long @rb_sysopen - /tmp/...
    // the maximum tempfile's name is 170 chars, but we also add a UUID to it.
    this.sanitizedFileName = new FileNameSanitizer(this.filename).call().substring(0,50)
    this.uploadAvatarCallback = options.uploadAvatarCallback
    this.uploadingAvatarCallback = options.uploadingAvatarCallback
    this.bucketName = options.bucketName
  }

  call(): UploadTask {
    const params = this._generateAwsParams()
    return this._upload(params)
  }

  _upload(params: UploadFileInput): UploadTask {
    const { completion, task } = startUpload(params, this.uploadingAvatarCallback)

    void completion
      .then(url => this.uploadAvatarCallback({ url }))
      .catch((error: unknown) => {
        const uploadError = normalizeStorageError(error)

        if (uploadError.name === 'RequestTimeTooSkewed') {
          const toastrOptions = {
            closeButton: true,
            timeOut: 0,
            extendedTimeOut: 0
          }

          toastr.error('Please adjust the system time of your device. Then try to publish again.', 'Avatar cannot be uploaded!', toastrOptions)
        }
        if (!window.Rollbar) { return }
        window.Rollbar.error(uploadError)
      })

    return task
  }

  _generateAwsParams(): UploadFileInput {
    const fileExtension = this.filename.split('.').pop()

    // Characters That Might Require Special Handling
    // https://docs.aws.amazon.com/en_us/AmazonS3/latest/dev/UsingMetadata.html
    const key = encodeURI(`${this.sanitizedFileName}_${uuidv4()}.${fileExtension}`)

    return {
      body: this.file,
      bucket: this.bucketName,
      key,
    }
  }
}

export default AvatarUploader
